import {
	add,
	eachDayOfInterval,
	endOfMonth,
	isSameDay,
	startOfMonth,
	startOfToday,
	subDays,
} from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useCalendarDaysForPeriod } from '@/features/travel-period'
import { useAppConfigStore } from '@/stores/app-store-config/appConfigStore'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { useStepStore } from '@/stores/step-store/stepStore'

export const useTravelPeriodQuery = () => {
	const urlQuery = useAppConfigStore.use.urlQuery()
	const setUrlQuery = useAppConfigStore.use.setUrlQuery()
	const setTravelPeriod = useCartStore.use.setTravelPeriod()
	const { startDate, endDate, nights } = useCartStore.use.travelPeriod()
	const setNextStep = useStepStore.use.setNextStep()

	const today = startOfToday()

	const startOfCurrentMonth = startOfMonth(startDate ?? today)

	const startOfNextMonth = add(startOfCurrentMonth, { months: 1 })
	const endOfNextMonth = endOfMonth(startOfNextMonth)

	const { data } = useCalendarDaysForPeriod(startOfCurrentMonth, endOfNextMonth)

	const [checkIsValid, setCheckIsValid] = useState(false)
	const hasRun = useRef(false)
	const hasCheckRun = useRef(false)

	const getDayInfo = useCallback(
		(day: Date) => data?.find((info) => isSameDay(info.date, day)),
		[data]
	)

	useEffect(() => {
		if (hasRun.current) return
		if (!urlQuery) return

		const { startDate, endDate, ...restQuery } = urlQuery

		if (!startDate || !endDate) return

		setTravelPeriod(startDate, endDate)
		hasRun.current = true
		setUrlQuery(restQuery)
		setCheckIsValid(true)
	}, [urlQuery, setTravelPeriod, setUrlQuery])

	useEffect(() => {
		const hasUnbookableDateSelected = (startDate: Date) => {
			if (!startDate) return false

			if (!endDate) return false

			const dayBeforeEndDate = subDays(endDate, 1)

			const cantDepart = getDayInfo(dayBeforeEndDate)?.closedToDeparture

			if (cantDepart) return true

			const hasClosedDays = eachDayOfInterval({
				start: startDate,
				end: dayBeforeEndDate,
			}).some((date) => !getDayInfo(date) || getDayInfo(date)?.closed)

			if (hasClosedDays) return true

			return Boolean(getDayInfo(endDate)?.closed && getDayInfo(dayBeforeEndDate)?.closed)
		}

		if (!data || !startDate) return
		if (!checkIsValid) return
		if (hasCheckRun.current) return

		if (hasUnbookableDateSelected(startDate)) {
			hasCheckRun.current = true
			return
		}

		const startDay = data?.find((day) => isSameDay(day.date, startDate))
		if (!startDay?.minNights && !startDay?.closedToArrival) {
			setNextStep()
		}

		if (startDay?.minNights && startDay.minNights <= nights && !startDay?.closedToArrival) {
			setNextStep()
		}

		hasCheckRun.current = true
	}, [checkIsValid, data, endDate, getDayInfo, nights, setNextStep, startDate])
}
